import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { TRAVEL_MODES, type TravelMode } from "@poi-planner/shared";
import { TRAVEL_MODE_ICONS, TRAVEL_MODE_LABELS } from "../../utils/geo";
import { Button } from "../Button";

export type TravelModeChoice = TravelMode | "auto";

interface TravelModePickerProps {
  /** Mode currently shown for the pin (after any drive fallback) */
  mode: TravelMode;
  /** The pin's setting: "auto" or an explicit override */
  choice: TravelModeChoice;
  /** What "auto" resolves to for this hop */
  autoMode: TravelMode;
  disabled?: boolean;
  onChange: (choice: TravelModeChoice) => void;
}

const CHOICES: TravelModeChoice[] = ["auto", ...TRAVEL_MODES];

/**
 * The travel card's mode icon doubles as an inline menu: pressing the icon
 * lists Auto / Walk / Transit / Drive. Read-only trips get the plain icon.
 */
export function TravelModePicker({
  mode,
  choice,
  autoMode,
  disabled = false,
  onChange,
}: TravelModePickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Focus the current choice when the menu opens
  useEffect(() => {
    if (!open) return;
    itemRefs.current[CHOICES.indexOf(choice)]?.focus();
  }, [open, choice]);

  // A press anywhere outside the picker (including elsewhere in the card)
  // closes the menu
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open]);

  if (disabled) {
    return (
      <span
        className="material-symbols-outlined text-base"
        aria-label={`${TRAVEL_MODE_LABELS[mode]}`}
        role="img"
      >
        {TRAVEL_MODE_ICONS[mode]}
      </span>
    );
  }

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const select = (next: TravelModeChoice) => {
    onChange(next);
    close();
  };

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    const index = itemRefs.current.findIndex(
      (item) => item === document.activeElement,
    );
    if (event.key === "Escape") {
      // Close only the menu, not the whole travel card
      event.stopPropagation();
      close();
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      const next = (index + step + CHOICES.length) % CHOICES.length;
      itemRefs.current[next]?.focus();
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  };

  const label = (option: TravelModeChoice) =>
    option === "auto"
      ? `Auto (${TRAVEL_MODE_LABELS[autoMode]})`
      : TRAVEL_MODE_LABELS[option];

  return (
    <div ref={rootRef} className="relative">
      <Button
        ref={triggerRef}
        variant="unstyled"
        className={clsx(
          "flex items-center rounded-lg border border-border bg-surface py-0.5 pl-1 pr-0.5 text-primary transition-colors hover:bg-subtle",
          open && "bg-subtle",
        )}
        aria-label={`Travel mode: ${label(choice)}. Change mode`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="material-symbols-outlined text-base" aria-hidden>
          {TRAVEL_MODE_ICONS[mode]}
        </span>
        <span
          className="material-symbols-outlined calendar-travel-menu-icon text-tertiary"
          aria-hidden
        >
          expand_more
        </span>
      </Button>

      {open && (
        <div
          role="menu"
          aria-label="Travel mode"
          className="absolute left-0 top-full z-10 mt-1 flex w-44 flex-col gap-0.5 rounded-lg border border-border bg-surface p-1 shadow-lg"
          onKeyDown={onMenuKeyDown}
        >
          {CHOICES.map((option, index) => {
            const selected = option === choice;
            const icon =
              TRAVEL_MODE_ICONS[option === "auto" ? autoMode : option];
            return (
              <Button
                key={option}
                ref={(node) => {
                  itemRefs.current[index] = node;
                }}
                variant="unstyled"
                role="menuitemradio"
                aria-checked={selected}
                className={clsx(
                  "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-subtle focus-visible:bg-subtle focus-visible:outline-none",
                  selected
                    ? "font-bold text-primary"
                    : "font-semibold text-secondary",
                )}
                onClick={() => select(option)}
              >
                <span
                  className="material-symbols-outlined calendar-travel-menu-icon"
                  aria-hidden
                >
                  {icon}
                </span>
                <span className="flex-1">{label(option)}</span>
                {selected && (
                  <span
                    className="material-symbols-outlined calendar-travel-menu-icon text-info"
                    aria-hidden
                  >
                    check
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}
